import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { Store } from '@ngrx/store';
import { map, Observable, switchMap, throwError } from 'rxjs';
import { authenticationQuery } from '../../../shared/state/selectors/authentication/authentication.selectors';
import { BookingForm } from '../interfaces/booking-form.interface';
import { BookingFormList } from '../interfaces/booking-form-list.interface';
import { CreateBookingForm } from '../interfaces/create-booking-form.interface';
import { UpdateBookingForm } from '../interfaces/update-booking-form.interface';

interface DeleteBookingFormResponse {
  success: boolean;
  uuid: string;
}

@Injectable({ providedIn: 'root' })
export class BookingFormsService {
  private _http = inject(HttpClient);
  private _store = inject(Store);

  /**
   * Creates a draft booking form for the authenticated supplier.
   * Pass `quoteUuid` to import the quote's costs, or `enquiryUuid` to start with blank costs.
   *
   * @param body The source identifier and any authored fields.
   * @returns An observable that emits the created draft booking form.
   */
  createBookingForm(body: CreateBookingForm): Observable<BookingForm> {
    return this._store.select(authenticationQuery.getUser).pipe(
      switchMap(({ userId }) => {
        return this._http.post<BookingForm>(`${environment.supplierApiGateway}/sup/account/${userId}/booking-forms`, body, {
          headers: {
            'Content-Type': 'application/ate.organiser.v1+json'
          }
        });
      })
    );
  }

  /**
   * Lists booking forms for the authenticated supplier, filtered to one enquiry.
   * Used to enforce a single active booking form per enquiry (Draft/Issued/Signed).
   *
   * @param enquiryUuid The enquiry to list booking forms for.
   * @returns An observable that emits the paged list of booking forms.
   */
  listBookingForms(enquiryUuid: string): Observable<BookingFormList> {
    if (!enquiryUuid) {
      return throwError(() => new Error('No enquiryUuid provided when trying to list booking forms'));
    }

    return this._store.select(authenticationQuery.getUser).pipe(
      switchMap(({ userId }) => {
        // No status filter: the gateway only allows Draft/Issued there, but we need Signed too,
        // so fetch every form for the enquiry and count client-side. A high limit avoids paging.
        const params = new HttpParams().set('enquiryUuid', enquiryUuid).set('limit', '100');
        return this._http.get<BookingFormList>(`${environment.supplierApiGateway}/sup/account/${userId}/booking-forms`, {
          params,
          headers: {
            'Content-Type': 'application/ate.organiser.v1+json'
          }
        });
      })
    );
  }

  /**
   * Account-wide paged list of booking forms for the Documents → Bookings page.
   * Unlike {@link listBookingForms} this is not scoped to an enquiry — it returns every
   * booking form for the supplier, one page at a time (the endpoint is slow, so pages are small).
   *
   * @param page 1-based page index.
   * @param limit Page size.
   * @returns An observable that emits the paged list of booking forms.
   */
  listAllBookingForms(page: number, limit: number): Observable<BookingFormList> {
    return this._store.select(authenticationQuery.getUser).pipe(
      switchMap(({ userId }) =>
        this._http.get<BookingFormList>(`${environment.supplierApiGateway}/sup/account/${userId}/booking-forms`, {
          params: new HttpParams().set('page', String(page)).set('limit', String(limit)),
          headers: {
            'Content-Type': 'application/ate.organiser.v1+json'
          }
        })
      )
    );
  }

  /**
   * Gets a single booking form by uuid for the authenticated supplier.
   *
   * @param bookingFormUuid The uuid of the booking form to fetch.
   * @returns An observable that emits the booking form.
   */
  getBookingForm(bookingFormUuid: string): Observable<BookingForm> {
    if (!bookingFormUuid) {
      return throwError(() => new Error('No bookingFormUuid provided when trying to get a booking form'));
    }

    return this._store.select(authenticationQuery.getUser).pipe(
      switchMap(({ userId }) => {
        return this._http.get<BookingForm>(`${environment.supplierApiGateway}/sup/account/${userId}/booking-forms/${bookingFormUuid}`, {
          headers: {
            'Content-Type': 'application/ate.organiser.v1+json'
          }
        });
      })
    );
  }

  /**
   * Updates an existing booking form for the authenticated supplier.
   *
   * @param bookingFormUuid The uuid of the booking form to update.
   * @param body The editable fields to persist.
   * @returns An observable that emits the updated booking form.
   */
  updateBookingForm(bookingFormUuid: string, body: UpdateBookingForm): Observable<BookingForm> {
    if (!bookingFormUuid) {
      return throwError(() => new Error('No bookingFormUuid provided when trying to update a booking form'));
    }

    return this._store.select(authenticationQuery.getUser).pipe(
      switchMap(({ userId }) => {
        return this._http.patch<BookingForm>(`${environment.supplierApiGateway}/sup/account/${userId}/booking-forms/${bookingFormUuid}`, body, {
          headers: {
            'Content-Type': 'application/ate.organiser.v1+json'
          }
        });
      })
    );
  }

  /**
   * Issues a draft booking form (Draft → Issued) for the authenticated supplier.
   * The gateway assigns a sequential reference and notifies the organiser.
   *
   * @param bookingFormUuid The uuid of the booking form to issue.
   * @returns An observable that emits the issued booking form.
   */
  issueBookingForm(bookingFormUuid: string): Observable<BookingForm> {
    if (!bookingFormUuid) {
      return throwError(() => new Error('No bookingFormUuid provided when trying to issue a booking form'));
    }

    return this._store.select(authenticationQuery.getUser).pipe(
      switchMap(({ userId }) => {
        return this._http.post<BookingForm>(
          `${environment.supplierApiGateway}/sup/account/${userId}/booking-forms/${bookingFormUuid}/issue`,
          {},
          {
            headers: {
              'Content-Type': 'application/ate.organiser.v1+json'
            }
          }
        );
      })
    );
  }

  /**
   * Deletes a booking form (MP-10980) for the authenticated supplier. The gateway permits this
   * only while the form is Draft or Issued; a Signed form is locked and rejected server-side.
   *
   * @param bookingFormUuid The uuid of the booking form to delete.
   * @returns An observable that emits a success response.
   */
  deleteBookingForm(bookingFormUuid: string): Observable<DeleteBookingFormResponse> {
    if (!bookingFormUuid) {
      return throwError(() => new Error('No bookingFormUuid provided when trying to delete a booking form'));
    }

    return this._store.select(authenticationQuery.getUser).pipe(
      switchMap(({ userId }) => {
        return this._http.delete<DeleteBookingFormResponse>(
          `${environment.supplierApiGateway}/sup/account/${userId}/booking-forms/${bookingFormUuid}`,
          {
            headers: {
              'Content-Type': 'application/ate.organiser.v1+json'
            }
          }
        );
      })
    );
  }

  /**
   * Cancels a signed booking form (Signed → Cancelled, MP-11051). Like a cancelled invoice, the form
   * and its PDF are kept for good and never change again; only the status moves. The gateway rejects
   * any form that isn't Signed with a 403.
   *
   * @param bookingFormUuid The uuid of the signed booking form to cancel.
   * @returns An observable that emits the cancelled booking form.
   */
  cancelBookingForm(bookingFormUuid: string): Observable<BookingForm> {
    if (!bookingFormUuid) {
      return throwError(() => new Error('No bookingFormUuid provided when trying to cancel a booking form'));
    }

    return this._store.select(authenticationQuery.getUser).pipe(
      switchMap(({ userId }) => {
        return this._http.post<BookingForm>(
          `${environment.supplierApiGateway}/sup/account/${userId}/booking-forms/${bookingFormUuid}/cancel`,
          {},
          {
            headers: {
              'Content-Type': 'application/ate.organiser.v1+json'
            }
          }
        );
      })
    );
  }

  /**
   * Returns a short-lived signed URL to download the booking form as a PDF (MP-10054),
   * rendered by the gateway from the form's current fields.
   */
  downloadBookingForm(bookingFormUuid: string): Observable<{ downloadUrl: string }> {
    if (!bookingFormUuid) {
      return throwError(() => new Error('No bookingFormUuid provided when trying to download a booking form'));
    }

    return this._store.select(authenticationQuery.getUser).pipe(
      switchMap(({ userId }) =>
        this._http
          .get<{ data: { downloadUrl: string } }>(
            `${environment.supplierApiGateway}/sup/account/${userId}/booking-forms/${bookingFormUuid}/download`,
            {
              headers: {
                'Content-Type': 'application/ate.organiser.v1+json'
              }
            }
          )
          .pipe(map(response => response?.data ?? { downloadUrl: '' }))
      )
    );
  }
}
